// =============================================================================
// domain/review.ts — the weekly and monthly retrospective.
// -----------------------------------------------------------------------------
// A FREE-TIER surface (the upgrade card: "Best Opps and Review moved to Free"),
// and until this landed it rendered "Not ported yet" in the client that actually
// launches (D56).
//
// ── 🚨 1. A REVIEW IS IDENTIFIED BY ITS PERIOD AND ITS OWNER — NEVER BY AN ID
//        BUILT FROM ITS CONTENT (D179) ──
//
// The old client minted `` `${cadence}-${periodStart}` `` — **`week-2026-08-09`,
// the same string for every user in the same week** — over `reviews.id`, which is
// a GLOBAL TEXT PRIMARY KEY. The first person to save a given week claimed that
// id for the whole table; every other user's upsert then resolved to
// `ON CONFLICT DO UPDATE` against a row RLS would not let them touch, and raised
//
//     new row violates row-level security policy (USING expression)
//     for table "reviews"
//
// permanently, for that week, weekly and monthly, for everyone who was not first.
// Measured on the live database 2026-08-14, not inferred.
//
// ▶ So `findReview` matches on **cadence + periodStart**, which is what a review
// actually IS, and `newReview` takes an id that the caller mints with real
// randomness. Legacy rows keep their old ids and keep loading, because
// `period_start` survives the round trip — nothing is migrated.
//
// ⚠ DO NOT reintroduce `${cadence}-${periodStart}` as an identity. It is
// documented here only so that an old row read as `week-2026-05-03` is
// recognisable for what it is. `io/sync/persistedIdShape.test.ts` fails the build
// if the shape comes back.
//
// ── 🚨 2. THE WEEK STARTS ON SUNDAY, AND THE GLOBAL RANGE FILTER'S DOES NOT ──
//
// The old client's `startOfWeek` is `x.setDate(x.getDate() - x.getDay())`, and
// `getDay()` counts Sunday as 0. `selectors/range`'s `week` preset is
// MONDAY-based. They are different windows and both are deliberate — the same
// split `domain/goals` already carries, with the same warning: reusing the range
// filter's week here would look obviously right and be wrong by a day at each
// end, and it would AGREE with beta on most weeks, because a futures trader's
// Saturday and Sunday are usually empty. Agreement by coincidence is not
// verification.
//
// ⚠ `weekStartOf` in `domain/processMetrics` IS the same Sunday rule and is
// reused here rather than written a second time.
//
// ── ⚠ 3. NO CLOCK (D26) ──
//
// "This week" is a date the caller passes in. A period that read `new Date()`
// would give a different answer inside an Edge Function than on the screen, and
// would make every test here time-dependent.
// =============================================================================

import { ADHERENCE_SERIES, adherenceRate, weekStartOf, type ProcessTrade } from './processMetrics';

export type ReviewCadence = 'week' | 'month';

/** Inclusive ISO bounds. `start` is a Sunday for `week`, the 1st for `month`. */
export interface ReviewPeriod {
  start: string;
  end: string;
}

/**
 * A stored review.
 *
 * ⚠ `answers` is keyed by `ReflectionPrompt.key` and holds RAW text. Nothing here
 * escapes it — React escapes on render, and the markdown brief is read by a model
 * rather than by a browser.
 */
export interface Review {
  id: string;
  cadence: ReviewCadence;
  periodStart: string;
  periodEnd: string;
  answers: Readonly<Record<string, string>>;
  /** ISO timestamps from the row. Null on a review that has never been saved. */
  createdAt: string | null;
  updatedAt: string | null;
}

// ── dates ────────────────────────────────────────────────────────────────────

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** ⚠ Local midnight, as everywhere in this client. */
const isoToDate = (iso: string): Date => new Date(`${iso}T00:00:00`);

const pad = (n: number): string => String(n).padStart(2, '0');

const dateToIso = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Is this a date the period math can actually use?
 *
 * 🚨 THE OLD CLIENT DOES NOT ASK. Its date input writes `e.target.value`
 * straight into the tab's state, and clearing that field — which a native date
 * input allows with one keystroke — sends `''` through `isoToDate`, producing an
 * Invalid Date and a heading that reads `NaN-NaN-NaN`. The screen does not
 * recover until the tab is left and re-entered.
 */
export function isReviewDate(value: string | null | undefined): boolean {
  if (!value || !ISO_DATE.test(value)) return false;
  return !Number.isNaN(isoToDate(value).getTime());
}

/** The Sunday-to-Saturday week, or the calendar month, containing `dateIso`. */
export function reviewPeriod(cadence: ReviewCadence, dateIso: string): ReviewPeriod {
  if (cadence === 'month') {
    const d = isoToDate(dateIso);
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    // ⚠ Day 0 of the NEXT month is the last day of this one — the old client's
    // `setMonth(m + 1, 0)`, which is correct for February and for leap years
    // without a table.
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start: dateToIso(start), end: dateToIso(end) };
  }
  const start = weekStartOf(dateIso) ?? dateIso;
  const endD = isoToDate(start);
  endD.setDate(endD.getDate() + 6);
  return { start, end: dateToIso(endD) };
}

/**
 * The date `Prev`/`Next` should move the anchor to.
 *
 * 🚨 A DELIBERATE DEVIATION, AND IT FIXES A REAL NAVIGATION DEFECT (PD-31).
 *
 * The old client shifts the ANCHOR: `d.setMonth(d.getMonth() + 1)`. From the
 * 31st of January that lands on **March 3rd**, so pressing `Next month` skips
 * February entirely — reachable on the seven 31-day months for anyone who opens
 * the tab late in the month, and it looks like the button simply moved twice.
 *
 * ▶ This shifts the PERIOD instead, which is what the button says it does: the
 * anchor becomes the first day of the neighbouring month. A week is unaffected
 * either way (±7 days cannot overflow), and is written the same way so the two
 * cadences do not diverge in how they are reasoned about.
 */
export function shiftReviewDate(
  cadence: ReviewCadence, dateIso: string, steps: number,
): string {
  const { start } = reviewPeriod(cadence, dateIso);
  const d = isoToDate(start);
  if (cadence === 'week') d.setDate(d.getDate() + 7 * steps);
  else d.setMonth(d.getMonth() + steps, 1);
  return dateToIso(d);
}

/**
 * ⚠ An explicit table rather than `toLocaleDateString`, for the same reason
 * `processMetrics.weekLabel` uses one: the label is parity surface, and a
 * locale-dependent formatter renders a different string on a machine set to
 * en-GB while every test on this box still passes.
 */
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** `Aug 9 – Aug 15, 2026` for a week · `August 2026` for a month. */
export function reviewPeriodLabel(cadence: ReviewCadence, p: ReviewPeriod): string {
  const s = isoToDate(p.start);
  if (cadence === 'month') return `${MONTHS_LONG[s.getMonth()]} ${s.getFullYear()}`;
  const e = isoToDate(p.end);
  // ⚠ EN DASH, and only the END carries the year — the old client's exact
  // shape, and the shape in Josh's screenshot.
  return `${MONTHS_SHORT[s.getMonth()]} ${s.getDate()} – `
    + `${MONTHS_SHORT[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
}

// ── the reflection prompts ───────────────────────────────────────────────────

export interface ReflectionPrompt {
  /** The `answers` key. ⚠ Stored — renaming one orphans everything written under it. */
  key: string;
  section: string;
  label: string;
  placeholder: string;
}

/**
 * Ten prompts in four groups, transcribed from the old client.
 *
 * ⚠ THE PLACEHOLDERS ARE TEACHING COPY, NOT FILLER. "Be concrete — 'no trading
 * in the first 5 minutes' beats 'be more patient'" is the instruction the prompt
 * is actually giving; shortening it would remove the lesson and leave the
 * question.
 */
export const REFLECTION_PROMPTS: readonly ReflectionPrompt[] = [
  {
    section: 'Performance', key: 'whatWorked',
    label: 'What worked this period?',
    placeholder: 'Setups, decisions, or patterns that paid off',
  },
  {
    section: 'Performance', key: 'whatBroke',
    label: 'What broke?',
    placeholder: 'Patterns that bled, decisions that backfired',
  },
  {
    section: 'Process', key: 'planFollowed',
    label: 'Did I follow my plan?',
    placeholder: 'Where I stayed disciplined; where I deviated and why',
  },
  {
    section: 'Process', key: 'mostCommonMistake',
    label: 'Most common mistake — and the trigger?',
    placeholder: 'e.g. chasing after a missed entry; revenge after stopping out',
  },
  {
    section: 'Process', key: 'aPlusAdherence',
    label: "A+ adherence — am I taking the trades I'm supposed to?",
    placeholder: 'Or settling for B-grade setups out of boredom',
  },
  {
    section: 'Mindset', key: 'emotionalBaseline',
    label: 'Emotional baseline this period.',
    placeholder: 'Calm / hot / focused / scattered / tilted',
  },
  {
    section: 'Mindset', key: 'externalFactors',
    label: 'Sleep, stress, life — anything that leaked into trading?',
    placeholder: 'Patterns you noticed in your own state',
  },
  {
    section: 'Forward-Looking', key: 'oneChange',
    label: "ONE specific behavior I'm changing next period.",
    placeholder: 'Be concrete — "no trading in the first 5 minutes" beats "be more patient"',
  },
  {
    section: 'Forward-Looking', key: 'patternToCut',
    label: "One pattern I'm cutting or scaling down.",
    placeholder: 'Look at the leaderboard for negative-expectancy patterns',
  },
  {
    section: 'Forward-Looking', key: 'patternToPress',
    label: "One pattern I'm pressing.",
    placeholder: 'Highest-expectancy pattern with enough sample size',
  },
];

/** The prompts, grouped, in the order they render. */
export function promptSections(): { section: string; prompts: ReflectionPrompt[] }[] {
  const out: { section: string; prompts: ReflectionPrompt[] }[] = [];
  for (const p of REFLECTION_PROMPTS) {
    const last = out[out.length - 1];
    if (last && last.section === p.section) last.prompts.push(p);
    else out.push({ section: p.section, prompts: [p] });
  }
  return out;
}

// ── identity ─────────────────────────────────────────────────────────────────

/**
 * The stored review for this period, or null.
 *
 * 🚨 MATCHED BY CADENCE + PERIOD START, NEVER BY ID — see the header. A legacy
 * row saved under `week-2026-05-03` is found by this, because `period_start`
 * carries the same fact the id was trying to encode.
 *
 * ⚠ Weekly and monthly must not merge. A month whose 1st is a Sunday has a week
 * with the same `periodStart`, and matching on the date alone would hand the
 * monthly review the weekly one's answers.
 */
export function findReview(
  reviews: readonly Review[], cadence: ReviewCadence, periodStart: string,
): Review | null {
  return reviews.find((r) => r.cadence === cadence && r.periodStart === periodStart) ?? null;
}

/**
 * A review that has never been saved.
 *
 * @param id must be minted with real randomness by the caller — `domain/` is pure
 * (D47) and cannot reach `Math.random()` without breaking the property that lets
 * these modules run inside an Edge Function. That is also why it is not built
 * from `cadence` and `periodStart`: see the header, and D179.
 */
export function newReview(
  id: string, cadence: ReviewCadence, period: ReviewPeriod,
): Review {
  return {
    id,
    cadence,
    periodStart: period.start,
    periodEnd: period.end,
    answers: {},
    createdAt: null,
    updatedAt: null,
  };
}

/**
 * `Not yet saved`, or when it was last written. ⚠ The formatted time is injected
 * (D26) — `toLocaleString` is the caller's business, and a clock here would make
 * this untestable.
 *
 * ⚠ THE OLD CLIENT ALSO TESTS `updatedAt !== createdAt`, AND THAT CLAUSE MUST NOT
 * BE COPIED. It is there because beta stamps both fields the moment it invents
 * the in-memory review, so "equal" is how it recognises one that was never
 * written. Here an unwritten review simply has `updatedAt: null`, and both
 * timestamps come off the stored row — where a first INSERT legitimately makes
 * them equal to the millisecond. Porting the clause literally would report a
 * review that had just been saved as `Not yet saved`.
 */
export function reviewSavedLabel(r: Review, formatted: string | null): string {
  if (!r.updatedAt || !formatted) return 'Not yet saved';
  return `Last edited ${formatted}`;
}

// ── the summary ──────────────────────────────────────────────────────────────

/**
 * What the four cards need from a trade.
 *
 * ⚠ It EXTENDS `ProcessTrade` so `adherenceRate` — the one function that decides
 * what "plan followed" means — is reused rather than reimplemented. Every field
 * is already on the trades table read; nothing here widens it (D128/D129).
 */
export interface ReviewTrade extends ProcessTrade {
  /**
   * The trade’s result IN THE DISPLAY UNIT — dollars, points, ticks or R.
   *
   * ⚠ RENAMED FROM `netPnl`, which was a lie waiting to happen: the caller now
   * feeds it whichever unit the toolbar is set to, and a field called `netPnl`
   * holding points is exactly how a later reader adds a dollar sign to it.
   *
   * ⚠ Null where the trade has no value in that unit. It is summed with `?? 0`
   * below, which is right for a TALLY — a trade with no R contributes nothing to
   * a pattern’s total — and would be wrong in a cell.
   */
  value: number | null;
  patternId: string | null;
}

export interface PatternTally {
  id: string;
  name: string;
  count: number;
  pnl: number;
}

export interface MistakeTally {
  tag: string;
  count: number;
}

/**
 * The process card.
 *
 * 🚨 THE FOUR RATES ARE `number | null`, AND THE NULL IS THE POINT (PD-21).
 *
 * The old client writes `adherenceRate(...) ?? 0`, so a period in which nothing
 * has been annotated reports **0%** — "you followed your plan on none of your
 * trades" — about a trader who has simply not filled the review in yet. The rate
 * correctly returned null and the renderer threw it away. D118, and the same
 * defect D130 removed from the Analytics overview.
 */
export interface ReviewAdherence {
  /** Keyed by `ADHERENCE_SERIES.key`: plan · aPlus · stop · target. */
  rates: Readonly<Record<string, number | null>>;
  revenge: number;
  fomo: number;
  overtraded: number;
}

export interface ReviewSummary {
  /** Best three by P&L, tagged patterns only. */
  topPatterns: readonly PatternTally[];
  /** Worst three, LOSING ones only — a period with no losing pattern has none. */
  bottomPatterns: readonly PatternTally[];
  /** Five most-used mistake tags. */
  topMistakes: readonly MistakeTally[];
  adherence: ReviewAdherence;
}

const flagged = (trades: readonly ReviewTrade[], flag: string): number =>
  trades.filter((t) => t.booleans?.[flag] === true).length;

/**
 * The four cards, over the trades the period actually contains.
 *
 * @param patternName id → current name. ⚠ A pattern deleted since the trade was
 * tagged reads `(deleted)` — the old client's word, and deliberately NOT Best
 * Opps' `(no pattern)`: there, the FK nulls the link and the opp genuinely has no
 * pattern; here the trade still names one that has since gone.
 *
 * ⚠ TIES ARE BROKEN BY NAME, which the old client does not do — it leaves them
 * in whatever order the trades happened to arrive in, and this client reads its
 * rows ordered by `id`, so "the same order" is not available to be matched. A
 * documented, stable order beats an arbitrary one that changes between loads.
 */
export function reviewSummary(
  trades: readonly ReviewTrade[],
  patternName: (id: string) => string | null,
): ReviewSummary {
  const byPattern = new Map<string, PatternTally>();
  for (const t of trades) {
    const pid = t.patternId;
    if (!pid) continue;
    const tally = byPattern.get(pid)
      ?? { id: pid, name: patternName(pid) ?? '(deleted)', count: 0, pnl: 0 };
    tally.count += 1;
    tally.pnl += t.value ?? 0;
    byPattern.set(pid, tally);
  }
  const patterns = [...byPattern.values()];

  const topPatterns = patterns.slice()
    .sort((a, b) => b.pnl - a.pnl || a.name.localeCompare(b.name))
    .slice(0, 3);
  const bottomPatterns = patterns.slice()
    .sort((a, b) => a.pnl - b.pnl || a.name.localeCompare(b.name))
    .filter((p) => p.pnl < 0)
    .slice(0, 3);

  const tagCounts = new Map<string, number>();
  for (const t of trades) {
    for (const tag of t.mistakeTags ?? []) {
      tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
    }
  }
  const topMistakes = [...tagCounts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag))
    .slice(0, 5);

  const rates: Record<string, number | null> = {};
  for (const s of ADHERENCE_SERIES) rates[s.key] = adherenceRate(trades, s.flag);

  return {
    topPatterns,
    bottomPatterns,
    topMistakes,
    adherence: {
      rates,
      revenge: flagged(trades, 'revengeTrade'),
      fomo: flagged(trades, 'fomoTrade'),
      overtraded: flagged(trades, 'overtraded'),
    },
  };
}

// ── the brief ────────────────────────────────────────────────────────────────

/**
 * ⚠ LOCAL FORMATTERS, and the duplication is deliberate — the same call
 * `domain/accountComparison` already makes. `ui/format.ts` is presentation and
 * `domain/` may not import it (D47); these produce the identical strings because
 * both were ported from the same three functions in the old client.
 */
const money = (n: number | null | undefined, decimals = 2): string => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const sign = v < 0 ? '-' : v > 0 ? '+' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  })}`;
};

const pctText = (n: number | null | undefined, decimals = 0): string =>
  (n === null || n === undefined || !Number.isFinite(Number(n)))
    ? '—' : `${Number(n).toFixed(decimals)}%`;

/** What the brief needs from the KPI aggregate. A subset of `Kpis`. */
export interface ReviewBriefKpis {
  count: number;
  totalPnl: number;
  winRate: number;
  profitFactor: number;
  expectancy: number;
}

/**
 * The marked-up levels, as `domain/journal.KeyLevels` stores them.
 *
 * ⚠ SPELLED OUT rather than typed `Record<string, number | null>`. An interface
 * without an index signature is not assignable to one in TypeScript, so a
 * `Record` here would force every caller to cast `KeyLevels` — and a cast is
 * exactly how a renamed level field starts reading as "not marked up".
 */
export interface ReviewLevels {
  pdVah: number | null;
  pdVal: number | null;
  pdPoc: number | null;
  prevHigh: number | null;
  prevLow: number | null;
  overnightHigh: number | null;
  overnightLow: number | null;
  callWall1: number | null;
  callWall2: number | null;
  putWall1: number | null;
  putWall2: number | null;
  hvl: number | null;
  mvc: number | null;
  lis: number | null;
  lisSummary: string;
}

/** ⚠ `number | string | null` — a legacy row stores a text label. See `saAverage`. */
export interface ReviewSelfAssessment {
  sleepHours: number | null;
  caffeineCups: number | null;
  energy: number | string | null;
  focus: number | string | null;
  mood: number | string | null;
  stress: number | string | null;
}

/** One journal entry, in the shape `domain/journal` already models. */
export interface ReviewJournalEntry {
  date: string;
  preMarketPlan: {
    marketRegime: string;
    gammaRegime: string;
    overallBias: string;
    marketRegimeAnalysis: string;
    gammaRegimeAnalysis: string;
    levels: ReviewLevels;
    selfAssessment: ReviewSelfAssessment;
    hypotheses: readonly {
      setup: string;
      patterns: readonly { id: string; name: string }[];
    }[];
  };
  eodRecap: { whatWorked: string; whatBroke: string; lessons: string };
}

export interface ReviewBriefInput {
  review: Review;
  kpis: ReviewBriefKpis;
  summary: ReviewSummary;
  /** Entries whose date falls inside the period, oldest first. */
  journals: readonly ReviewJournalEntry[];
  /** id → CURRENT name, so a rename propagates into the brief. */
  patternNames: ReadonlyMap<string, string>;
}

type LevelKey = Exclude<keyof ReviewLevels, 'lisSummary'>;

/** A level line, dropping anything not marked up. `null` reads as "not marked". */
function levelLine(levels: ReviewLevels, pairs: readonly [string, LevelKey][]): string {
  const parts: string[] = [];
  for (const [label, key] of pairs) {
    const v = levels[key];
    if (typeof v === 'number' && Number.isFinite(v)) parts.push(`${label} ${v}`);
  }
  return parts.join(' · ');
}

/**
 * The pre-market plan as markdown bullets — the old client's
 * `formatPreMarketStructured(pmp, { boldLabels: false })`.
 *
 * ⚠ The auction levels and the dealer-positioning levels are two SEPARATE lines
 * on purpose: the old client's note says the two contexts stay visually distinct
 * in the prompt, and merging them would hand the model one undifferentiated list.
 */
function planLines(plan: ReviewJournalEntry['preMarketPlan']): string[] {
  const out: string[] = [];
  if (plan.marketRegime) out.push(`- Market regime: ${plan.marketRegime}`);
  if (plan.gammaRegime) out.push(`- Gamma regime: ${plan.gammaRegime}`);
  if (plan.overallBias) out.push(`- Overall bias: ${plan.overallBias}`);

  const auction = levelLine(plan.levels, [
    ['PrevHi', 'prevHigh'], ['PrevLo', 'prevLow'],
    ['ONHi', 'overnightHigh'], ['ONLo', 'overnightLow'],
    ['PD VAH', 'pdVah'], ['PD POC', 'pdPoc'], ['PD VAL', 'pdVal'],
  ]);
  if (auction) out.push(`- Levels: ${auction}`);

  const gamma = levelLine(plan.levels, [
    ['MVC', 'mvc'], ['CW1', 'callWall1'], ['CW2', 'callWall2'],
    ['HVL', 'hvl'], ['PW1', 'putWall1'], ['PW2', 'putWall2'],
  ]);
  if (gamma) out.push(`- Gamma levels: ${gamma}`);

  const lis = plan.levels.lis;
  const lisNum = typeof lis === 'number' && Number.isFinite(lis) ? String(lis) : '';
  const lisSum = (plan.levels.lisSummary ?? '').trim();
  if (lisNum || lisSum) {
    out.push(`- LIS: ${lisNum}${lisSum ? (lisNum ? ` — ${lisSum}` : lisSum) : ''}`);
  }
  return out;
}

/**
 * The hypotheses as bullets — the old client's `formatSetupsHunting`.
 *
 * ⚠ NUMBERED BY POSITION, blanks included, exactly as `dayReport.planRecapLines`
 * does: "Hypo 1" and "Hypo 3" with no 2 says the middle one was left empty,
 * which is information. Renumbering the survivors would claim there were two.
 *
 * ⚠ A pattern deleted since the plan was written keeps its stored name and is
 * marked `(deleted)`. The live name wins for one that still exists, so a rename
 * propagates.
 */
function hypothesisLines(
  plan: ReviewJournalEntry['preMarketPlan'],
  patternNames: ReadonlyMap<string, string>,
): string[] {
  const out: string[] = [];
  plan.hypotheses.forEach((h, i) => {
    const names = h.patterns.map((p) => {
      const live = patternNames.get(p.id);
      return live ?? `${p.name || '?'} (deleted)`;
    });
    const setup = (h.setup || '').trim();
    if (!names.length && !setup) return;
    const head = names.length ? names.join(', ') : '(no patterns)';
    out.push(`- Hypo ${i + 1}: ${head}${setup ? ` — ${setup}` : ''}`);
  });
  return out;
}

/**
 * The average of a self-assessment field across the days that reported it.
 *
 * ⚠ NON-NUMERIC RATINGS ARE DROPPED, not coerced. A legacy row can store the
 * text label `'High'` where the sliders now write `4`, and `Number('High')` is
 * NaN — which would turn the whole average into `NaN` and print it as a real
 * figure. Dropping one day's text rating understates the sample; poisoning the
 * mean misstates the trader.
 */
function saAverage(
  days: readonly ReviewSelfAssessment[], key: keyof ReviewSelfAssessment,
): number | null {
  const vals = days
    .map((d) => d[key])
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/**
 * Did this day report anything at all?
 *
 * 🚨 `Object.keys(sa).length` — the old client's test — CANNOT BE PORTED
 * LITERALLY. Beta stores `selfAssessment` as `{}` until a slider is touched, so
 * counting keys answers "did they fill it in". This client's reader always builds
 * all six fields, nulls included (`domain/journal`), so the key count is
 * permanently six and the brief would announce "5 of 5 days reported" over a week
 * nobody assessed, with every average showing a dash beside it.
 *
 * ▶ Same question, asked of the VALUES.
 */
const saReported = (sa: ReviewSelfAssessment): boolean =>
  Object.values(sa).some((v) => v !== null && v !== '');

/**
 * The whole brief, as markdown.
 *
 * ── 🚨 IT BUILDS A PROMPT, IT DOES NOT SEND ONE ──
 *
 * Pure text assembly, so nothing here can spend money. Every coach call is billed
 * and burns a capped run; what decides WHETHER to send lives in the UI behind a
 * button. The same string is what `Copy as Markdown` puts on the clipboard, which
 * is why the two buttons cannot drift apart.
 *
 * ⚠ THE UNSAVED TYPING IS INCLUDED. Both buttons hand in a review whose `answers`
 * were read out of the textareas, not off the last save — a trader who writes a
 * reflection and presses `Coach Me` without saving must not be coached on the
 * empty version of it.
 */
export function buildReviewBrief(input: ReviewBriefInput): string {
  const { review, kpis: k, summary, journals, patternNames } = input;
  const lines: string[] = [];

  lines.push(
    `# ${review.cadence === 'week' ? 'Weekly' : 'Monthly'} review — `
    + `${review.periodStart} → ${review.periodEnd}`,
    '',
  );
  lines.push(
    `**Trades:** ${k.count} | **P&L:** ${money(k.totalPnl)} | `
    + `**Win rate:** ${pctText(k.winRate)} | `
    // ⚠ `∞` is a real answer — winners and no losers — not a divide-by-zero to
    // hide. `computeKpis` returns `Infinity` deliberately and says so.
    + `**PF:** ${k.profitFactor === Infinity ? '∞' : k.profitFactor.toFixed(2)} | `
    + `**Expectancy:** ${money(k.expectancy)}`,
    '',
  );

  if (summary.topPatterns.length) {
    lines.push('## Top patterns by P&L');
    for (const p of summary.topPatterns) {
      lines.push(`- **${p.name}**: ${p.count} trades · ${money(p.pnl)}`);
    }
    lines.push('');
  }
  if (summary.bottomPatterns.length) {
    lines.push('## Bleeding patterns');
    for (const p of summary.bottomPatterns) {
      lines.push(`- **${p.name}**: ${p.count} trades · ${money(p.pnl)}`);
    }
    lines.push('');
  }
  if (summary.topMistakes.length) {
    lines.push('## Most common mistakes');
    for (const m of summary.topMistakes) lines.push(`- \`${m.tag}\`: ${m.count}`);
    lines.push('');
  }

  // ⚠ ALWAYS PRESENT, even when every figure is a dash. An absent adherence
  // section reads to the model as a period with nothing to say about process;
  // "not annotated" is itself the finding, and the dash says which it is.
  const a = summary.adherence;
  lines.push('## Process adherence');
  lines.push(
    `- Plan followed: ${pctText(a.rates['plan'])} · A+ setup: ${pctText(a.rates['aPlus'])}`
    + ` · Stop honored: ${pctText(a.rates['stop'])}`
    + ` · Target honored: ${pctText(a.rates['target'])}`,
  );
  lines.push(`- Revenge: ${a.revenge} · FOMO: ${a.fomo} · Overtraded: ${a.overtraded}`);
  lines.push('');

  if (journals.length) {
    lines.push('## Journal excerpts');
    const saDays = journals
      .map((j) => j.preMarketPlan.selfAssessment)
      .filter(saReported);
    if (saDays.length) {
      const avg = (key: keyof ReviewSelfAssessment): string => {
        const v = saAverage(saDays, key);
        return v == null ? '—' : v.toFixed(1);
      };
      lines.push(
        `**Self-assessment averages (${saDays.length} of ${journals.length} days reported):** `
        + `sleep ${avg('sleepHours')}h · caffeine ${avg('caffeineCups')}c · `
        + `mood ${avg('mood')} · focus ${avg('focus')} · energy ${avg('energy')} · `
        + `stress ${avg('stress')}`,
        '',
      );
    }

    for (const j of journals) {
      lines.push(`### ${j.date}`);
      const p = j.preMarketPlan;
      if (p.marketRegimeAnalysis) lines.push(`- Market regime analysis: ${p.marketRegimeAnalysis}`);
      if (p.gammaRegimeAnalysis) lines.push(`- Gamma regime analysis: ${p.gammaRegimeAnalysis}`);
      lines.push(...planLines(p));
      lines.push(...hypothesisLines(p, patternNames));

      const sa = p.selfAssessment;
      const state: string[] = [];
      const stateNum = (
        key: keyof ReviewSelfAssessment, suffix: string, label: string,
      ): void => {
        const v = sa[key];
        if (typeof v === 'number' && Number.isFinite(v)) state.push(`${label} ${v}${suffix}`);
      };
      stateNum('sleepHours', 'h', 'sleep');
      stateNum('mood', '/5', 'mood');
      stateNum('focus', '/5', 'focus');
      stateNum('energy', '/5', 'energy');
      stateNum('stress', '/5', 'stress');
      if (state.length) lines.push(`- State: ${state.join(' · ')}`);

      if (j.eodRecap.whatWorked) lines.push(`- Worked: ${j.eodRecap.whatWorked}`);
      if (j.eodRecap.whatBroke) lines.push(`- Broke: ${j.eodRecap.whatBroke}`);
      if (j.eodRecap.lessons) lines.push(`- Lessons: ${j.eodRecap.lessons}`);
      lines.push('');
    }
  }

  lines.push('## My reflection');
  for (const prompt of REFLECTION_PROMPTS) {
    const ans = (review.answers[prompt.key] ?? '').trim();
    if (ans) lines.push(`**${prompt.label}**`, ans, '');
  }

  lines.push('## Coach me');
  lines.push(
    'Given the stats and my reflection above, what\'s the single highest-leverage '
    + `thing I should focus on for the next ${review.cadence}? Be direct.`,
  );
  return `${lines.join('\n')}\n`;
}
